import type { PopulatedOrgMember, User } from "@api/types";
import { ApiError, handleApiError } from "@api/utils";
import { useAuthStore } from "@lib/auth";
import useMatchMutate from "@ui/hooks/match-mutate";
import { useCallback, useMemo, useState } from "react";
import { type Comment, commentService, useComments } from "@/api/comment";
import { useOrgMembers } from "@/api/org-member";
import { useConfirmDialog } from "@/components/confirm-dialog";
import * as analytics from "@/lib/analytics";

export interface CommentThreadTarget {
  gym: string;
  /** Comment on a task (comments also surface in the task's history). */
  task?: string;
  /** Comment directly on a contact: taskless notes for the whole team. */
  customer?: string;
  /** Resolved elsewhere by hosts that already fetch members; optional. */
  members?: PopulatedOrgMember[];
}

/**
 * Data and actions behind a comment thread: the list (oldest first), the
 * author lookup, add, and delete-your-own with confirmation. Hosts supply
 * the presentation; the task drawer and the contact notes tab render the
 * same thread differently.
 */
export function useCommentThread({
  gym,
  task,
  customer,
  members: membersProp,
}: CommentThreadTarget) {
  const matchMutate = useMatchMutate();
  const authUser = useAuthStore((state) => state.user);
  const org = useAuthStore((state) => state.org);
  const { confirm, dialog } = useConfirmDialog();
  const [busy, setBusy] = useState(false);

  // High limit so authors resolve even in large orgs (no populate support
  // on the comments API).
  const { data: fetchedMembers } = useOrgMembers(
    org?.id ?? "",
    { limit: 100 },
    { skip: !org?.id || !!membersProp },
  );
  const members = membersProp ?? fetchedMembers;

  const { data, isLoading, mutate } = useComments(
    task
      ? { gym, task, sort: "createdAt" }
      : customer
        ? { gym, customer, sort: "createdAt" }
        : undefined,
  );

  // Contact mode is for taskless notes; the customer filter also matches
  // comments denormalized from the contact's tasks, so drop those here.
  const comments = useMemo(
    () => (task ? data : data?.filter((comment) => !comment.task)),
    [data, task],
  );

  // Include the signed-in user so their own comments resolve even when they
  // aren't in the gym's member list (e.g. org-level admins).
  const userById = useMemo(() => {
    const map = new Map<string, User>(
      (members ?? []).map((member) => [member.user.id, member.user]),
    );
    if (authUser && !map.has(authUser.id)) map.set(authUser.id, authUser);
    return map;
  }, [members, authUser]);

  const add = useCallback(
    async (body: string): Promise<boolean> => {
      const trimmed = body.trim();
      if (!trimmed || busy) return false;
      try {
        setBusy(true);
        const created = await commentService.create({ gym, task, customer, body: trimmed });
        analytics.track("Comment Added", { gym, on: task ? "task" : "contact" });
        // Show the new comment right away; revalidate in the background.
        await mutate((current) => [...(current ?? []), created as Comment], {
          revalidate: true,
        });
        // Task comments also feed the task's history/audit trail.
        if (task) await matchMutate(/\/tasks/);
        return true;
      } catch (err) {
        if (err instanceof ApiError) handleApiError(err);
        return false;
      } finally {
        setBusy(false);
      }
    },
    [busy, gym, task, customer, mutate, matchMutate],
  );

  const remove = useCallback(
    async (comment: Comment, labels: { title: string; description: string }) => {
      const confirmed = await confirm({
        ...labels,
        confirmLabel: "Delete",
        cancelLabel: "Cancel",
        variant: "destructive",
      });
      if (!confirmed) return;
      try {
        await commentService.readAndDelete(comment.id);
        analytics.track("Comment Deleted", { gym, on: task ? "task" : "contact" });
        await mutate((current) => current?.filter((item) => item.id !== comment.id), {
          revalidate: true,
        });
      } catch (err) {
        if (err instanceof ApiError) handleApiError(err);
      }
    },
    [confirm, gym, task, mutate],
  );

  const update = useCallback(
    async (comment: Comment, body: string): Promise<boolean> => {
      const trimmed = body.trim();
      if (!trimmed) return false;
      // Nothing changed: treat as saved so the host can leave edit mode.
      if (trimmed === comment.body) return true;
      try {
        const updated = await commentService.readAndUpdate(comment.id, { body: trimmed });
        analytics.track("Comment Edited", { gym, on: task ? "task" : "contact" });
        await mutate(
          (current) =>
            current?.map((item) => (item.id === comment.id ? (updated as Comment) : item)),
          { revalidate: true },
        );
        return true;
      } catch (err) {
        if (err instanceof ApiError) handleApiError(err);
        return false;
      }
    },
    [gym, task, mutate],
  );

  const isOwn = useCallback((comment: Comment) => comment.user === authUser?.id, [authUser?.id]);

  return { comments, isLoading, busy, userById, add, update, remove, isOwn, dialog };
}
